/**
 * Consecutive error counter with localStorage persistence
 * Stores all error counts in a single object for efficient storage
 */

import { FatalErrors } from "@classdojo/monsterverse-utils";

import * as LocalStorageUtils from "./LocalStorageUtils";

/**
 * Supported error types for consecutive counting
 */
const _ERROR_TYPES = {
  ...FatalErrors,
} as const;

/**
 * Storage key for all error counters
 */
const STORAGE_KEY = "monsterverse_error_storage";

/**
 * Type for the error storage object
 */
type ErrorStorage = { [key: string]: number };
type ErrorTypeKeys = keyof typeof _ERROR_TYPES;

/**
 * In-memory cache to guarantee consistency and eliminate race conditions
 */
let memoryCache: ErrorStorage | null = null;

/**
 * Get the current error storage object from cache, initializing from localStorage if needed
 */
function getErrorStorage(): ErrorStorage {
  if (memoryCache === null) {
    // Initialize cache from localStorage on first access
    memoryCache = LocalStorageUtils.getItem(STORAGE_KEY, {});
  }
  return memoryCache;
}

/**
 * Save the error storage object to both cache and localStorage
 */
function setErrorStorage(storage: ErrorStorage): void {
  memoryCache = storage;
  LocalStorageUtils.setItem(STORAGE_KEY, storage);
}

/**
 * Get the consecutive error count for a specific error type
 * @param errorType The error type to get the count for
 * @returns The current count for the error type (0 if not found)
 */
export function getConsecutiveError(errorType: ErrorTypeKeys): number {
  const storage = getErrorStorage();
  return storage[errorType] ?? 0;
}

/**
 * Increment the consecutive error count for a specific error type
 * @param errorType The error type to increment
 * @returns The new count after incrementing
 */
export function incrementConsecutiveError(errorType: ErrorTypeKeys): number {
  const storage = getErrorStorage();
  const newCount = (storage[errorType] ?? 0) + 1;
  const newStorage = { ...storage, [errorType]: newCount };
  setErrorStorage(newStorage);
  return newCount;
}

/**
 * Reset the consecutive error count for a specific error type
 * @param errorType The error type to reset
 */
export function resetConsecutiveError(errorType: ErrorTypeKeys): void {
  const storage = getErrorStorage();
  const newStorage = { ...storage };
  delete newStorage[errorType];
  setErrorStorage(newStorage);
}
